export const QUIZ_IMAGE_ACCEPT = "image/jpeg,image/png,image/gif,image/webp";
export const QUIZ_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

const getApiOrigin = () => {
  const apiBase = process.env.REACT_APP_DEV_API_URL || "";
  try {
    return new URL(apiBase, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const QUOTE_CHARS = /^["'\u201C\u201D\u2018\u2019]+|["'\u201C\u201D\u2018\u2019]+$/g;
const ENCODED_QUOTE = /^%22/i;

/** True when Java rejected a filesystem path (quoted filename in DB). */
export const isQuizImagePathError = (message = "") => {
  const text = String(message || "");
  return /illegal char/i.test(text) && /["'<>]|%22/i.test(text);
};

/** Strip JSON/quotes accidentally stored around filenames from Java API. */
export const sanitizeQuestionImageUrl = (rawUrl = "") => {
  let trimmed = String(rawUrl ?? "").trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.includes("%22")) {
    try {
      trimmed = decodeURIComponent(trimmed);
    } catch {
      /* keep original */
    }
  }

  let previous = "";
  while (trimmed !== previous) {
    previous = trimmed;
    trimmed = trimmed
      .replace(ENCODED_QUOTE, "")
      .replace(QUOTE_CHARS, "")
      .replace(/%22$/i, "")
      .trim();
  }

  return trimmed;
};

/** Turns backend-relative paths into absolute API URLs for fetch/img. */
export const resolveQuestionImageUrl = (rawUrl = "") => {
  const trimmed = sanitizeQuestionImageUrl(rawUrl);
  if (!trimmed) {
    return "";
  }

  if (/^(data:|https?:)/i.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return `${getApiOrigin()}${trimmed}`;
  }

  const apiBase = process.env.REACT_APP_DEV_API_URL || "";
  const normalizedBase = apiBase.endsWith("/") ? apiBase : `${apiBase}/`;
  return `${normalizedBase}${trimmed.replace(/^\//, "")}`;
};

/** External URLs and data URLs can be rendered directly on an img tag. */
export const isDirectDisplayImageUrl = (url = "") => {
  const trimmed = String(url || "").trim();
  if (!trimmed) {
    return false;
  }

  if (trimmed.startsWith("data:")) {
    return true;
  }

  if (!/^https?:\/\//i.test(trimmed)) {
    return false;
  }

  try {
    return new URL(trimmed).origin !== getApiOrigin();
  } catch {
    return true;
  }
};

export const getQuestionImageSrc = (question = {}) => {
  const uploadSrc = sanitizeQuestionImageUrl(question.imageUploadDataUrl || "");
  if (uploadSrc) {
    return uploadSrc;
  }
  return sanitizeQuestionImageUrl(question.imageUrl || "");
};

export const getResolvedQuestionImageSrc = (question = {}) => {
  const src = getQuestionImageSrc(question);
  if (!src || src.startsWith("data:")) {
    return src;
  }
  return resolveQuestionImageUrl(src);
};

export const hasQuestionImage = (question = {}) => Boolean(getQuestionImageSrc(question));

export const readImageFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read image file"));
    reader.readAsDataURL(file);
  });

export const validateQuizImageFile = (file) => {
  if (!file) {
    return "No image selected";
  }

  if (!file.type.startsWith("image/")) {
    return "Only image files are allowed (JPEG, PNG, GIF, WebP)";
  }

  if (file.size > QUIZ_IMAGE_MAX_BYTES) {
    return "Image must be 2 MB or smaller";
  }

  return "";
};

export const dataUrlToBlob = (dataUrl) => {
  const [header, base64] = String(dataUrl).split(",");
  if (!header || !base64) {
    return null;
  }

  const mimeMatch = header.match(/data:(.*?);base64/);
  const mime = mimeMatch?.[1] || "application/octet-stream";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
};
